import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { withPrefix } from 'gatsby';
import { useDispatch } from 'react-redux';
import { Location, WindowLocation } from '@reach/router';
import Header from '../components/Header';
import Footer from '../components/Footer';
import useBrowsingHistory from '@ecri/react-components/hooks/browsinghistory';
import { ROUTE_CHANGED } from '../store/analytics/types';
import '@ecri/styles/scss/ecri.scss';
import './layout.scss';

interface LayoutProps {
  analyticsSiteId: number;
  title?: string;
  description?: string;
  searchDescription?: string;
  keywords?: string;
  children?: any;
}

interface TemplateProps extends LayoutProps {
  location: WindowLocation;
}

const metaImage = `${process.env.GATSBY_ASSET_CDN_SITE}/images1/ecri-trusted-voice-healthcare.jpg`;

const TemplateWrapper: React.FC<TemplateProps> = ({
  analyticsSiteId,
  title = 'ECRI - Advancing Safe and Effective Healthcare Globally',
  description = '',
  searchDescription = '',
  keywords = 'ECRI, ECRI Institute, supply chain management, patient safety, risk management',
  children,
  location,
}) => {
  const providedDescription =
    description.trim() !== '' ? description : searchDescription;

  const metaDesc =
    providedDescription !== ''
      ? providedDescription
      : 'ECRI is committed to protecting patients from unsafe and ineffective medical technologies and practices. We offer evidence-based research and health technology assessments; supply chain management solutions; patient safety, risk, and quality support; and consulting to healthcare organizations worldwide.';

  const dispatch = useDispatch();
  const [, addBrowsingHistoryEntry] = useBrowsingHistory();

  useEffect(() => {
    if (
      !location.pathname.startsWith(withPrefix('/auth-callback')) &&
      !location.pathname.startsWith(withPrefix('silent_renew'))
    ) {
      if (!location.pathname.startsWith(withPrefix('log-out'))) {
        try {
          addBrowsingHistoryEntry({
            Title: title,
            Url: location.pathname.replace(withPrefix('/'), '/'),
            Virtual: true,
          });
        } catch (err) {
          //sometimes the cookie values cannot be properly encoded
          //we'll just ignore the errors for now
        }
      }
      dispatch({
        type: ROUTE_CHANGED,
        payload: {
          title,
          analyticsSiteId,
          location,
        },
      });
    }
  }, [location]);

  return (
    <>
      <Helmet title={title}>
        <meta name="title" content={title} />
        <meta name="description" content={metaDesc} />
        <meta name="keywords" content={keywords} />
        <meta property="og:type" content="Article" />
        <meta property="og:site_name" content="ECRI" />
        <meta property="og:title" content={title} />
        {location && location.href && (
          <meta property="og:url" content={metaImage} />
        )}
        <meta property="og:image" content={metaImage} />
        {metaDesc !== '' && (
          <meta property="og:description" content={metaDesc} />
        )}

        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={title} />
        {metaDesc !== '' && (
          <meta name="twitter:description" content={metaDesc} />
        )}
        {location && location.href && (
          <meta name="twitter:url" content={location.href} />
        )}
        <meta name="twitter:image" content={metaImage} />
      </Helmet>
      <Header location={location} />
      <main>{children}</main>
      <Footer />
    </>
  );
};

const Layout = (props: LayoutProps) => (
  <Location>
    {({ location }) => <TemplateWrapper location={location} {...props} />}
  </Location>
);

export default Layout;
